import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useDropzone } from "react-dropzone";
import { useSnackbar } from "notistack";
import { formatFileSize } from "../utils/utils";
import { Box, Typography } from "@mui/material";
import { Upload, FileText, Trash2 } from "lucide-react";

const DEFAULT_ACCEPT = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/msword": [".doc"],
};

const FileDropzone = ({
  handleFileInput,
  filesLimit,
  initialFiles,
  handleFileDelete,
  deleteJustImportedFile,
  fileName,
  type,
  label,
  accept,
  fileTypesLabel,
  ...props
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [uploadedFileName, setUploadedFileName] = useState("");
  const [isFileDeleted, setIsFileDeleted] = useState(false);

  const isMulti = filesLimit > 1;

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: accept || DEFAULT_ACCEPT,
    maxSize: 12582912,
    multiple: filesLimit > 1,
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        let rejectionReasons = fileRejections.map((f) => f.errors[0]?.code);

        if (rejectionReasons.includes("file-too-large")) {
          let rejectedFile = fileRejections.find(
            (f) => f.errors[0]?.code === "file-too-large"
          ).file;
          enqueueSnackbar(
            intl.formatMessage(
              { id: "devise.signup.drop_reject" },
              {
                fileName: rejectedFile.path,
                sizeLimit: formatFileSize(12582912),
              }
            )
          );
        }

        if (rejectionReasons.includes("file-invalid-type")) {
          enqueueSnackbar(
            intl.formatMessage({ id: "devise.signup.drop_invalid_type" })
          );
        }

        return;
      }

      setUploadedFileName(fileName || acceptedFiles[0]?.name || "");
      handleFileInput(acceptedFiles);
    },
  });

  const handleDelete = (event) => {
    event.stopPropagation();
    setUploadedFileName("");
    setIsFileDeleted(true);
    if (handleFileDelete) {
      handleFileDelete();
    }
    if (deleteJustImportedFile) {
      deleteJustImportedFile();
    }
  };

  // Depend on whether there are initial files, not on the array itself: callers often pass a
  // fresh `[]` each render, which would otherwise wipe the name of a file the user just dropped.
  const hasInitialFiles = Boolean(initialFiles && initialFiles.length > 0);

  useEffect(() => {
    if (fileName && hasInitialFiles) {
      setUploadedFileName(fileName);
      setIsFileDeleted(false);
    } else {
      setUploadedFileName("");
    }
  }, [fileName, hasInitialFiles]);

  useEffect(() => {
    if (isFileDeleted) {
      setUploadedFileName("");
      setIsFileDeleted(false);
    }
  }, [isFileDeleted]);

  const accentColor = type === "candidate"
    ? "hsl(153, 68%, 25%)"
    : "hsl(178, 93%, 10%)";

  return (
    <Box {...getRootProps()} sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <input {...getInputProps()} disabled={filesLimit === 0} />

      {label && (
        <Typography sx={{
          fontSize: "12px",
          fontFamily: "Segoe UI Bold, sans-serif, system-ui",
          color: "hsl(178, 93%, 10%)",
          mb: "2px",
        }}>
          {label}
        </Typography>
      )}

      {uploadedFileName && !isMulti ? (
        <Box sx={{
          display: "flex", alignItems: "center", gap: "12px",
          p: "12px", borderRadius: "12px",
          bgcolor: "hsla(160, 10%, 96%, 0.3)",
          border: "1px solid hsla(160, 10%, 90%, 0.4)",
        }}>
          <FileText size={16} color={accentColor} style={{ flexShrink: 0 }} />
          <Typography sx={{
            flex: 1, fontSize: "12px", color: "hsl(178, 93%, 10%)",
            fontFamily: "Segoe UI Regular, sans-serif, system-ui",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {uploadedFileName}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={handleDelete}
            sx={{
              border: "none", bgcolor: "transparent", cursor: "pointer",
              p: 0, display: "flex",
              color: "hsl(178, 10%, 45%)",
              "&:hover": { color: "hsl(0, 65%, 51%)" },
              transition: "color 0.15s",
            }}
          >
            <Trash2 size={14} />
          </Box>
        </Box>
      ) : (
        <Box
          component="button"
          type="button"
          onClick={open}
          sx={{
            width: "100%", display: "flex", alignItems: "center",
            justifyContent: "center", gap: "8px",
            p: "12px", borderRadius: "12px",
            border: "2px dashed hsla(160, 10%, 90%, 0.6)",
            bgcolor: "transparent", color: "hsl(178, 10%, 45%)",
            cursor: "pointer",
            "&:hover": {
              borderColor: `${accentColor}66`,
              color: accentColor,
            },
            transition: "all 0.15s",
          }}
        >
          <Upload size={16} />
          <Typography sx={{
            fontSize: "12px",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          }}>
            {intl.formatMessage({ id: props.dropzoneTextId || "devise.signup.drag_and_drop_zone", defaultMessage: "Importer votre fichier" })}
          </Typography>
        </Box>
      )}

      <Typography sx={{
        fontSize: "10px", color: "hsl(178, 10%, 45%)",
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
      }}>
        {fileTypesLabel || intl.formatMessage({ id: "devise.signup.drop_file_types" })}
      </Typography>
    </Box>
  );
};

export default FileDropzone;
